import type { Selection } from '@/entities/searchCriteria';
import type { Country, Resort, Hotel } from '@/shared/model';

export function emptySelection(): Selection {
  return { kind: 'none' };
}

export function isNone(s: Selection): s is { kind: 'none' } {
  return s.kind === 'none';
}

export function isCountry(s: Selection): s is { kind: 'country'; country: Country } {
  return s.kind === 'country';
}

export function isResorts(s: Selection): s is { kind: 'resorts'; country: Country; resorts: Resort[] } {
  return s.kind === 'resorts';
}

export function isHotels(s: Selection): s is { kind: 'hotels'; country: Country; hotels: Hotel[] } {
  return s.kind === 'hotels';
}

export function selectCountry(country: Country, resorts: Resort[] = []): Selection {
  return { kind: 'country', country: { ...country, resorts } };
}

export function toggleResort(
  current: Selection,
  resort: Resort,
  country: Country,
  countryResorts: Resort[] = [],
): Selection {
  const isSameCountrySet = isResorts(current) && current.country.id === resort.countryId;

  if (isSameCountrySet) {
    const alreadySelected = current.resorts.some((item) => item.id === resort.id);

    if (alreadySelected) {
      const remaining = current.resorts.filter((item) => item.id !== resort.id);
      if (remaining.length === 0) {
        return { kind: 'country', country: { ...current.country, resorts: countryResorts } };
      }
      return { kind: 'resorts', country: current.country, resorts: remaining };
    }

    return { kind: 'resorts', country: current.country, resorts: [...current.resorts, resort] };
  }

  const isSameWholeCountry = isCountry(current) && current.country.id === resort.countryId;
  if (isSameWholeCountry) {
    return { kind: 'resorts', country: current.country, resorts: [resort] };
  }

  return { kind: 'resorts', country: { ...country, resorts: country.resorts ?? [] }, resorts: [resort] };
}

export function selectAllResorts(country: Country, resorts: Resort[] = []): Selection {
  return selectCountry(country, resorts);
}

export function selectHotel(_current: Selection, hotel: Hotel, country: Country, resorts?: Resort[]): Selection {
  return { kind: 'hotels', country: { ...country, resorts: resorts || [] }, hotels: [hotel] };
}

export function getSelectedCountry(s: Selection): Country | null {
  return isNone(s) ? null : s.country;
}

export function getSelectedResorts(s: Selection): Resort[] {
  return isResorts(s) ? s.resorts : [];
}

export function getSelectedHotels(s: Selection): Hotel[] {
  return isHotels(s) ? s.hotels : [];
}

// eslint-disable-next-line unicorn/prefer-export-from -- local re-export: export-from would collide with the type import above (no-duplicate-imports flags the same module imported + re-exported)
export type { Selection };
